import { useState } from 'react'
import { KeyRound, ChevronDown, ChevronUp } from 'lucide-react'
import type { Creds } from '@features/dashboards/testing-ground/types'
import { deriveEnv } from '@features/dashboards/testing-ground/utils'

interface LiveCredentialsPanelProps {
  creds: Creds
  onChange: (c: Creds) => void
  /** True once customerId/childCustomerId come from an existing customer's own
   * Scope data — those two fields are then read-only, only the API key is editable. */
  locked: boolean
}

const inputCls = 'w-full rounded-[8px] px-2.5 py-1.5 text-[12px] font-mono text-[#1e293b] disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:border-[#6366f1]'
const labelCls = 'block text-[10px] uppercase tracking-wider text-[#64748b] mb-1'

export function LiveCredentialsPanel({ creds, onChange, locked }: LiveCredentialsPanelProps) {
  const hasCreds = !!(creds.customerId && creds.apiKey)
  const [open, setOpen] = useState(!hasCreds)
  const env = deriveEnv(creds.apiBase)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<'ok' | 'error' | null>(null)

  // Only checks that the credentials themselves authenticate — it does NOT confirm
  // this customer has the Hosted OneSDK/IDV product entitlement, so "Connected" here
  // can still be followed by an access-denied error from an actual IDV call.
  async function handleTestConnection() {
    setTesting(true)
    setTestResult(null)
    try {
      const res = await fetch('/api/frankie-proxy/kyc/ruok', {
        headers: {
          'X-FT-Customer-ID': creds.customerId,
          'X-FT-API-Key': creds.apiKey,
          'X-FT-API-Base': creds.apiBase,
        },
      })
      setTestResult(res.ok ? 'ok' : 'error')
    } catch {
      setTestResult('error')
    } finally {
      setTesting(false)
      setTimeout(() => setTestResult(null), 4000)
    }
  }

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-2.5 transition-colors"
        style={{ background: hasCreds ? 'rgba(52,211,153,.06)' : 'rgba(251,191,36,.06)' }}
      >
        <KeyRound size={13} className={hasCreds ? 'text-emerald-600' : 'text-amber-600'} />
        <span className="text-[11px] font-semibold flex-1 text-left" style={{ color: hasCreds ? '#047857' : '#b45309' }}>
          {hasCreds ? 'Live credentials configured' : 'Configure live credentials to make real calls'}
        </span>
        {env && (
          <span
            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider"
            style={env === 'UAT'
              ? { background: 'rgba(251,191,36,.15)', color: '#b45309' }
              : { background: 'rgba(248,113,113,.15)', color: '#b91c1c' }}
          >
            {env}
          </span>
        )}
        {open ? <ChevronUp size={13} className="text-[#94a3b8]" /> : <ChevronDown size={13} className="text-[#94a3b8]" />}
      </button>
      {open && (
        <div className="p-4" style={{ borderTop: '1px solid #e5e7eb' }}>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Customer ID</label>
              <input
                value={creds.customerId}
                onChange={(e) => onChange({ ...creds, customerId: e.target.value })}
                disabled={locked}
                placeholder="your-customer-id"
                className={inputCls}
                style={{ background: locked ? '#f8fafc' : '#ffffff', border: '1px solid #e5e7eb' }}
              />
            </div>
            <div>
              <label className={labelCls}>Child Customer ID</label>
              <input
                value={creds.childCustomerId}
                onChange={(e) => onChange({ ...creds, childCustomerId: e.target.value })}
                disabled={locked}
                placeholder="Only if a child account"
                className={inputCls}
                style={{ background: locked ? '#f8fafc' : '#ffffff', border: '1px solid #e5e7eb' }}
              />
            </div>
            <div>
              <label className={labelCls}>API Key</label>
              <input
                type="password"
                value={creds.apiKey}
                onChange={(e) => onChange({ ...creds, apiKey: e.target.value })}
                placeholder="your-api-key"
                className={inputCls}
                style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}
              />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <button
              onClick={() => void handleTestConnection()}
              disabled={testing || !creds.customerId || !creds.apiKey}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] text-[11px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              style={{ background: 'rgba(52,211,153,.1)', border: '1px solid rgba(52,211,153,.3)', color: '#15803d' }}
            >
              {testing ? 'Testing…' : testResult === 'ok' ? '✓ Connected' : testResult === 'error' ? '✗ Failed' : 'Test connection'}
            </button>
            <p className="text-[10px] text-[#94a3b8] italic">
              Kept only in this browser (localStorage) — sent directly to FrankieOne&apos;s UAT sandbox via the existing proxy, never persisted against this deal. Testing only confirms the credentials authenticate, not that IDV/Hosted OneSDK is entitled for this customer.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
